import { useEffect, useRef, useState } from "react";
import { Search, Loader2, FileText, ArrowUpRight } from "lucide-react";
import { searchBriefsRemote, type BriefSearchResult } from "@/lib/agentcore";

/**
 * Semantic search across the signed-in user's OWN past briefs.
 *
 * The user types a natural-language question ("who is raising prices",
 * "competitors hiring in sales") and gets back ranked past briefs - matched by
 * meaning, not keywords - each clicking through to that competitor's detail page.
 *
 * Reuses the existing S3 Vectors store via searchBriefsRemote(). Debounced,
 * on-brand (dark/glass), with loading + empty states. Degrades silently: if the
 * backend returns null (feature unconfigured / local dev) the whole panel hides
 * itself so it never shows a broken control.
 */

interface BriefSearchProps {
  idToken: string;
  /** Open a competitor's detail page (mirrors the Dashboard navigation). */
  onOpen: (competitor: string) => void;
}

const DEBOUNCE_MS = 350;
const MIN_QUERY_LEN = 2;

export function BriefSearch({ idToken, onOpen }: BriefSearchProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<BriefSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  // null = feature unavailable (backend replied {ok:false} / unreachable) -> hide.
  const [available, setAvailable] = useState(true);
  // Track whether we have run at least one search for the current query, so the
  // empty state only appears after a real (completed) search.
  const [searched, setSearched] = useState(false);

  const reqId = useRef(0);

  useEffect(() => {
    const q = query.trim();
    if (q.length < MIN_QUERY_LEN) {
      setResults([]);
      setLoading(false);
      setSearched(false);
      return;
    }

    setLoading(true);
    const myReq = ++reqId.current;
    const handle = window.setTimeout(async () => {
      const res = await searchBriefsRemote(idToken, q, 8);
      // Ignore out-of-order responses (a newer keystroke already fired).
      if (myReq !== reqId.current) return;
      if (res === null) {
        setAvailable(false);
        setResults([]);
      } else {
        setAvailable(true);
        setResults(res);
      }
      setSearched(true);
      setLoading(false);
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(handle);
  }, [query, idToken]);

  // Feature unavailable (e.g. vector store not configured) - render nothing so the
  // dashboard degrades cleanly instead of showing a dead search box.
  if (!available) return null;

  const q = query.trim();
  const showEmpty = searched && !loading && q.length >= MIN_QUERY_LEN && results.length === 0;

  return (
    <section className="ns-glass rounded-2xl p-5 sm:p-6 shadow-card">
      <div className="flex items-center gap-2 mb-1">
        <Search className="w-3.5 h-3.5 text-[#22d3ee]" aria-hidden="true" />
        <span className="text-[10px] font-semibold text-[#22d3ee] uppercase tracking-[0.2em]">
          Search your briefs
        </span>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Ask in plain language - "who is raising prices", "who is hiring in sales" - and find it
        across everything you have analyzed.
      </p>

      {/* Search input */}
      <div className="relative">
        <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500">
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin text-[#22d3ee]" aria-hidden="true" />
          ) : (
            <Search className="w-4 h-4" aria-hidden="true" />
          )}
        </span>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search past briefs by meaning..."
          aria-label="Search your past briefs"
          className="w-full rounded-xl border border-[#1a2640] bg-white/[0.03] py-3 pl-10 pr-4 text-sm text-white placeholder:text-slate-600 outline-none transition-colors focus:border-[#22d3ee]/40 focus:bg-white/[0.05] min-h-[44px]"
        />
      </div>

      {/* Results */}
      {results.length > 0 && (
        <ul className="mt-4 flex flex-col gap-2.5">
          {results.map((r, i) => (
            <li key={`${r.competitor}-${r.date}-${i}`}>
              <button
                type="button"
                onClick={() => onOpen(r.competitor)}
                className="group flex w-full items-start justify-between gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-3.5 py-3 text-left transition-colors hover:border-[#22d3ee]/30 hover:bg-white/[0.04] cursor-pointer"
              >
                <div className="flex items-start gap-3 min-w-0">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#1a2640] bg-white/[0.03] shrink-0 text-slate-400">
                    <FileText className="w-4 h-4" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-sm font-bold text-white truncate group-hover:text-[#22d3ee] transition-colors">
                        {r.competitor}
                      </span>
                      {r.date && (
                        <span className="text-[11px] text-slate-600 shrink-0">{r.date}</span>
                      )}
                    </div>
                    {r.snippet && (
                      <p className="mt-0.5 text-[12px] text-slate-500 line-clamp-2 leading-relaxed">
                        {r.snippet}
                      </p>
                    )}
                  </div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-slate-600 group-hover:text-[#22d3ee] transition-colors shrink-0 mt-0.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Empty state - a real, completed search with no matches */}
      {showEmpty && (
        <p className="mt-4 text-center text-xs text-slate-600">
          No matching briefs yet - try different words, or run an analysis to build up your history.
        </p>
      )}
    </section>
  );
}
